/**
 * RestoMod Studio - Print & Dossier Formatting Utilities
 *
 * Provides print styling utilities, window.print helper, pagination break managers,
 * currency/date formatting, and insurance appraisal export functions.
 */

import {
  ValuationDossier,
  VehicleProfile,
  SubletExpense,
  LaborRecord,
  ProvenanceScoreBreakdown,
} from '../types/dossier';
import { BOMItem } from '../types/bom';
import {
  RestorationPhase,
  RESTORATION_PHASES,
  PhotoLog,
  SubAssembly,
  HardwareBag,
  RestorationTask,
} from '../types/teardown';
import {
  calculateProvenanceScore,
  calculateValuationSummary,
} from './budgetCalculations';

export const PRINT_PAGE_BREAK = 'page-break';
export const PRINT_AVOID_BREAK = 'avoid-break';
export const PRINT_NO_PRINT = 'no-print';

/**
 * Triggers browser print dialog safely.
 */
export function triggerPrint(): void {
  if (typeof window !== 'undefined' && typeof window.print === 'function') {
    try {
      window.print();
    } catch (err) {
      console.error('Failed to trigger window.print():', err);
    }
  } else {
    console.warn('window.print is unavailable in this environment.');
  }
}

/**
 * Formats numeric values to USD currency strings.
 */
export function formatCurrency(
  val: number,
  options?: { showCents?: boolean }
): string {
  const showCents = options?.showCents ?? false;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  }).format(val || 0);
}

/**
 * Formats ISO date or date string into readable standard format.
 */
export function formatShortDate(dateStr?: string): string {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  } catch {
    return dateStr;
  }
}

/**
 * Formats percentage number.
 */
export function formatPercent(val: number, decimals: number = 1): string {
  return `${(val || 0).toFixed(decimals)}%`;
}

/**
 * Translates provenance completeness score (0-100) into underwriting rating information.
 */
export function getProvenanceBandInfo(score: number): {
  band: ProvenanceScoreBreakdown['ratingBand'];
  badgeColor: string;
  badgeBg: string;
  badgeBorder: string;
  description: string;
} {
  if (score >= 90) {
    return {
      band: 'Elite Documentation',
      badgeColor: '#10b981',
      badgeBg: '#064e3b',
      badgeBorder: '#10b981',
      description:
        'Comprehensive receipt audit trail, complete 7-phase photographic evidence, and documented hardware bags. Preferred underwriter tier with immediate agreed-value approval recommendation.',
    };
  }
  if (score >= 75) {
    return {
      band: 'Strong Provenance',
      badgeColor: '#38bdf8',
      badgeBg: '#0c4a6e',
      badgeBorder: '#0284c7',
      description:
        'Verified VIN/cowl data, major phase milestones documented with before/after photos, and high receipt capture. Suitable for Hagerty/Grundy standard underwriting.',
    };
  }
  if (score >= 50) {
    return {
      band: 'Moderate Proof',
      badgeColor: '#f59e0b',
      badgeBg: '#78350f',
      badgeBorder: '#d97706',
      description:
        'Baseline vehicle identification and partial receipts. Underwriters may request additional photo documentation or physical inspection.',
    };
  }
  return {
    band: 'Preliminary Draft',
    badgeColor: '#94a3b8',
    badgeBg: '#1e293b',
    badgeBorder: '#475569',
    description:
      'Preliminary project logging. Additional milestone proof, hardware cataloging, and vendor receipts required for formal agreed-value binder.',
  };
}

/**
 * Assembles a complete ValuationDossier payload from application state.
 */
export interface BuildDossierParams {
  vehicle: VehicleProfile;
  bomItems: BOMItem[];
  subletExpenses: SubletExpense[];
  laborRecords: LaborRecord[];
  photoLogs: PhotoLog[];
  subAssemblies: SubAssembly[];
  hardwareBags: HardwareBag[];
  tasks: RestorationTask[];
  appreciationBufferPct?: number;
  declarations?: Partial<ValuationDossier['underwritingDeclarations']>;
}

export function buildValuationDossierData(
  params: BuildDossierParams
): ValuationDossier {
  const {
    vehicle,
    bomItems,
    subletExpenses,
    laborRecords,
    photoLogs,
    subAssemblies,
    hardwareBags,
    tasks,
    appreciationBufferPct = 12,
    declarations,
  } = params;

  const provenanceScore = calculateProvenanceScore({
    vehicle,
    photos: photoLogs,
    bomItems,
    subAssemblies,
    hardwareBags,
    subletExpenses,
  });

  const valuation = calculateValuationSummary({
    vehicle,
    bomItems,
    subletExpenses,
    laborRecords,
    provenanceScore,
    appreciationBufferPct,
  });

  // Compile Phase Matrix
  const phaseKeys = Object.keys(RESTORATION_PHASES) as RestorationPhase[];
  const phases = {} as ValuationDossier['phases'];

  for (const phase of phaseKeys) {
    const phaseTasks = tasks.filter((t) => t.phase === phase);
    phases[phase] = {
      metadata: RESTORATION_PHASES[phase],
      taskCount: phaseTasks.length,
      completedTaskCount: phaseTasks.filter((t) => t.status === 'completed').length,
      bomItemCount: bomItems.filter((i) => i.phase === phase).length,
      subAssemblyCount: subAssemblies.filter((s) => s.phase === phase).length,
      hardwareBagCount: hardwareBags.filter((b) => {
        const sub = subAssemblies.find((s) => s.id === b.subAssemblyId);
        return sub ? sub.phase === phase : false;
      }).length,
      photoCount: photoLogs.filter((p) => p.phase === phase).length,
    };
  }

  // BOM highlights: Major capital items sorted by actual or estimated cost
  const bomHighlights = [...bomItems]
    .sort((a, b) => {
      const costA = (a.actualCost || a.estimatedCost || 0) * (a.quantity || 1);
      const costB = (b.actualCost || b.estimatedCost || 0) * (b.quantity || 1);
      return costB - costA;
    })
    .slice(0, 15);

  // Curated photos: explicitly flagged or with milestone proof
  const curatedPhotos = photoLogs.filter(
    (p) => p.includeInDossier || p.milestoneProof
  );

  const defaultDeclarations: ValuationDossier['underwritingDeclarations'] = {
    preparedBy: declarations?.preparedBy || 'RestoMod Studio Appraisals & Provenance',
    intendedUnderwriter: declarations?.intendedUnderwriter || 'Hagerty',
    storageFacilityType:
      declarations?.storageFacilityType || 'Climate-Controlled Garage',
    annualEstimatedMileage: declarations?.annualEstimatedMileage ?? 2500,
    ownerSignatureDate:
      declarations?.ownerSignatureDate || new Date().toISOString().split('T')[0],
  };

  return {
    generatedAt: new Date().toISOString(),
    vehicle,
    valuation,
    phases,
    bomHighlights,
    subletExpenses,
    curatedPhotos,
    underwritingDeclarations: defaultDeclarations,
  };
}

/**
 * Exports complete ValuationDossier object as downloadable JSON file.
 */
export function exportDossierJson(dossier: ValuationDossier): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  try {
    const jsonStr = JSON.stringify(dossier, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeVin = dossier.vehicle.vin
      ? dossier.vehicle.vin.replace(/[^a-zA-Z0-9]/g, '')
      : 'VEHICLE';
    const dateStamp = new Date().toISOString().split('T')[0];

    link.href = url;
    link.download = `restomod_dossier_${safeVin}_${dateStamp}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Failed to export dossier JSON:', err);
  }
}
